import { useCallback, useRef, useState } from 'react'

import { useAppUserContext } from 'app/useAppUserContext'
import { useObject } from 'data/hooks/objects'
import { FieldIcon } from 'data/utils/fieldDefinitions'
import { getFieldIcon } from 'features/admin/fields/icons/utils'
import { sortFieldsByOrder } from 'features/fields/utils'

import useDeepEqualsMemoValue from 'v2/ui/utils/useDeepEqualsMemoValue'

import { useToast } from 'ui/components/Toast'

import { useToolbarContext } from './useToolbarContext'

const LABEL_MAX_LENGTH = 25

type FieldOption = {
    label: string
    sid: string
    isPrimary: boolean
    isEnabled: boolean
    isEditable: boolean
    icon?: FieldIcon
    field: FieldDto
}

export function useToolbarActionsFieldsState() {
    const { objectSid, onFieldCreated } = useToolbarContext()

    const { object, changeField, bulkChangeFields } = useObject(objectSid)

    const fields = sortFieldsByOrder(object?.fields ?? [], object)

    const toast = useToast()

    const toggleFieldState = useCallback(
        async (field: FieldOption, isEnabled: boolean) => {
            if (!field.isEditable) return

            try {
                changeField(
                    field.sid,
                    {
                        connection_options: {
                            ...field.field.connection_options,
                            is_disabled: !isEnabled,
                        },
                    },
                    { allowOptimisticUpdates: true }
                )
            } catch (error) {
                console.error(error)
                toast({
                    type: 'error',
                    title: 'There was a problem updating the field',
                    startIcon: { name: 'AlertCircle' },
                    helperText: 'Please try again later. If the issue persists, contact support.',
                })
            }
        },
        [changeField, toast]
    )

    const [searchQuery, setSearchQuery] = useState('')

    const filteredFields = fields.reduce((acc, field) => {
        if (field.label.toLowerCase().includes(searchQuery.toLowerCase())) {
            let label = field.label
            if (label.length > LABEL_MAX_LENGTH) {
                label = label.slice(0, LABEL_MAX_LENGTH) + '...'
            }

            acc.push({
                label,
                sid: field._sid,
                field: field,
                isPrimary: field.is_primary,
                isEnabled: !field.connection_options?.is_disabled,
                isEditable: isFieldEditable(field),
                icon: getFieldIcon(field),
            })
        }

        return acc
    }, [] as FieldOption[])

    const filteredFieldsRef = useRef(filteredFields)
    filteredFieldsRef.current = filteredFields

    const canToggleAll = filteredFields.some((field) => field.isEditable)
    const canToggleAllRef = useRef(canToggleAll)
    canToggleAllRef.current = canToggleAll

    const toggleAllFieldsState = useCallback(
        async (isEnabled: boolean) => {
            if (!canToggleAllRef.current) return

            const changes = filteredFieldsRef.current.reduce((acc, field) => {
                if (field.isEditable) {
                    acc.push({
                        _sid: field.sid,
                        connection_options: {
                            ...field.field.connection_options,
                            is_disabled: !isEnabled,
                        },
                    })
                }

                return acc
            }, [] as Partial<FieldDto>[])
            if (changes.length < 1) return

            try {
                await bulkChangeFields(changes)
            } catch (error) {
                console.error(error)
                toast({
                    type: 'error',
                    title: 'There was a problem updating the fields',
                    startIcon: { name: 'AlertCircle' },
                    helperText: 'Please try again later. If the issue persists, contact support.',
                })
            }
        },
        [bulkChangeFields, toast]
    )

    const onEnableAll = useCallback(() => {
        toggleAllFieldsState(true)
    }, [toggleAllFieldsState])

    const onDisableAll = useCallback(() => {
        toggleAllFieldsState(false)
    }, [toggleAllFieldsState])

    const { isAdmin } = useAppUserContext()
    const allowAddNewField = !object?.is_schema_read_only && isAdmin

    const addNewButtonRef = useRef<HTMLDivElement>(null)

    const [isFieldEditorOpen, setIsFieldEditorOpen] = useState(false)
    const onAddNewFieldClick = useCallback(() => {
        setIsFieldEditorOpen(true)
    }, [])

    const onFieldEditorSuccess = useCallback(
        (field: FieldDto) => {
            setIsFieldEditorOpen(false)
            onFieldCreated?.(field)
        },
        [onFieldCreated]
    )

    const onFieldEditorClose = useCallback(() => {
        setIsFieldEditorOpen(false)
    }, [])

    const [isDropdownOpen, setIsDropdownOpen] = useState(false)
    const onDropdownOpenChange = useCallback((open: boolean) => {
        setIsDropdownOpen(open)
        if (!open) {
            setIsFieldEditorOpen(false)
        }
    }, [])

    const dropdownContentRef = useRef<HTMLDivElement>(null)

    const isEmpty = filteredFields.length === 0 && !searchQuery

    return useDeepEqualsMemoValue({
        objectSid,
        fields: filteredFields,
        toggleFieldState,
        onEnableAll,
        onDisableAll,
        searchQuery,
        setSearchQuery,
        canToggleAll,
        dropdownContentRef,
        addNewButtonRef,
        isFieldEditorOpen,
        onAddNewFieldClick,
        onFieldEditorSuccess,
        onFieldEditorClose,
        isDropdownOpen,
        onDropdownOpenChange,
        allowAddNewField,
        isEmpty,
    })
}

function isFieldEditable(field: FieldDto) {
    const isPrimary = field.is_primary
    const isShadowed = field.connection_options.is_shadowed

    return !isPrimary && !isShadowed
}
